<template>
  <div
    ref="calculatorContainer"
    tabindex="0"
    @keydown="handleKeyDown"
    @click="focusCalculator"
  >
    <calculator-interface
      :current-value="displayValue"
      @button-click="handleClick"
    ></calculator-interface>
  </div>
</template>

<script>
import CalculatorInterface from './CalculatorInterface.vue';
import evaluate from './evaluate';

const OPERATORS = ['+', '-', '*', '/'];

export default {
  components: {
    CalculatorInterface,
  },

  data() {
    return {
      currentInput: '',
      inputTokens: [],
      errorMessage: '',
      cursorPosition: 0,
      showCursor: false,
      resultJustShown: false,
    };
  },
  computed: {
    currentValue() {
      if (this.errorMessage) return this.errorMessage;
      if (this.currentInput) return this.currentInput;
      return String(evaluate(this.inputTokens));
    },
    displayValue() {
      const value = this.currentValue;
      if (!this.showCursor || !this.currentInput) {
        return value;
      }
      const pos = Math.min(this.cursorPosition, this.currentInput.length);
      return this.currentInput.slice(0, pos) + '|' + this.currentInput.slice(pos);
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.focusCalculator();
    });
  },
  methods: {
    focusCalculator() {
      if (this.$refs.calculatorContainer) {
        this.$refs.calculatorContainer.focus();
      }
    },
    addTokensRecalculation() {
      if (this.inputTokens.length > 2 && this.currentInput === '' && this.inputTokens.length % 2 === 1) {
        this.inputTokens = [...this.inputTokens, ...this.inputTokens.slice(-2)];
      }
    },
    replaceTokensWithPartialResult() {
      if (this.inputTokens.length < 5  || this.inputTokens.length % 2 === 0) return;
      const partialResult = evaluate(this.inputTokens.slice(0, -2));
      this.inputTokens = [
        String(partialResult),
        ...this.inputTokens.slice(-2),
      ];
    },
    handleErrorMessage() {
      const result = evaluate(this.inputTokens);
      if (this.isErrorMessage(result)) {
        this.errorMessage = String(result);
        this.inputTokens = [];
      }
    },
    insertNewInput() {
      if (this.currentInput === '') return;

      const lastToken = this.inputTokens[this.inputTokens.length - 1];
      if (OPERATORS.includes(lastToken)) {
        this.inputTokens.push(this.currentInput);
        this.currentInput = '';
      }
    },
    resetForNewEntry() {
      this.currentInput = '';
      this.inputTokens = [];
      this.errorMessage = '';
      this.cursorPosition = 0;
      this.showCursor = true;
    },
    applyUnaryOperation(fn) {
      const hasInput = this.currentInput !== '';
      const baseValue = hasInput
        ? parseFloat(this.currentInput)
        : evaluate(this.inputTokens);
      const result = fn(baseValue);
      this.currentInput = String(result);
      this.cursorPosition = this.currentInput.length;
      this.showCursor = false;
      this.resultJustShown = true;
      if (!hasInput) {
        this.inputTokens = [];
      }
    },
    handleClick(button) {
      const parsed = parseFloat(button);

      if (!Number.isNaN(parsed)) {
        if (this.resultJustShown) {
          this.resetForNewEntry();
          this.resultJustShown = false;
        }
        this.insertAtCursor(String(parsed));
        if (this.errorMessage) this.errorMessage = '';
        this.resultJustShown = false;
        return;
      }

      if (button === '.') {
        if (this.resultJustShown) {
          this.resetForNewEntry();
          this.resultJustShown = false;
        }
        if (this.currentInput === '') {
          this.currentInput = '0.';
          this.cursorPosition = this.currentInput.length;
          this.showCursor = true;
        } else if (!this.currentInput.includes('.')) {
          this.insertAtCursor('.');
        }
        if (this.errorMessage) this.errorMessage = '';
        this.resultJustShown = false;
        return;
      }

      if (OPERATORS.includes(button)) {
        if (this.currentInput === '' && this.inputTokens.length === 0) {
          this.inputTokens.push('0');
        }
        if (this.currentInput !== '') {
          this.inputTokens.push(this.currentInput);
          this.currentInput = '';
          this.cursorPosition = 0;
          this.showCursor = false;
        }

        const mostRecentToken = this.inputTokens[this.inputTokens.length - 1];
        if (OPERATORS.includes(mostRecentToken)) {
          this.inputTokens.pop();
        }

        this.inputTokens.push(button);
        this.resultJustShown = false;
        return;
      }

      if (button === '=') {
        this.addTokensRecalculation();
        this.insertNewInput();
        this.replaceTokensWithPartialResult();
        this.handleErrorMessage();
        this.showCursor = false;
        this.cursorPosition = 0;
        this.resultJustShown = true;
        return;
      }

      if (button === 'AC') {
        this.currentInput = '';
        this.inputTokens = [];
        this.errorMessage = '';
        this.cursorPosition = 0;
        this.showCursor = false;
        this.resultJustShown = false;
        return;
      }

      if (button === '+/-') {
        this.applyUnaryOperation(v => -v);
        return;
      }

      if (button === '%') {
        this.applyUnaryOperation(v => v / 100);
        return;
      }

      if (button === '√') {
        this.applyUnaryOperation(v => Math.sqrt(v));
        return;
      }
    },

    isErrorMessage(result) {
      return (
        result === Infinity ||
        result === -Infinity ||
        Number.isNaN(result)
      );
    },
    insertAtCursor(char) {
      if (!this.showCursor) {
        this.showCursor = true;
        this.cursorPosition = this.currentInput.length;
      }
      if (this.currentInput === '0' && char !== '.') {
        this.currentInput = char;
        this.cursorPosition = 1;
      } else {
        const pos = Math.min(this.cursorPosition, this.currentInput.length);
        this.currentInput = this.currentInput.slice(0, pos) + char + this.currentInput.slice(pos);
        this.cursorPosition = pos + 1;
      }
    },
    deleteAtCursor() {
      if (!this.currentInput || !this.showCursor) {
        this.handleClick('AC');
        return;
      }
      if (this.cursorPosition > 0) {
        const pos = this.cursorPosition - 1;
        this.currentInput = this.currentInput.slice(0, pos) + this.currentInput.slice(pos + 1);
        this.cursorPosition = pos;
        if (this.currentInput === '') {
          this.showCursor = false;
          this.cursorPosition = 0;
        }
      }
    },
    moveCursor(direction) {
      if (!this.currentInput) {
        this.showCursor = true;
        this.cursorPosition = 0;
        return;
      }
      this.showCursor = true;
      if (direction === 'left') {
        this.cursorPosition = Math.max(0, this.cursorPosition - 1);
      } else if (direction === 'right') {
        this.cursorPosition = Math.min(this.currentInput.length, this.cursorPosition + 1);
      }
    },
    handleKeyDown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.moveCursor('left');
        return;
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.moveCursor('right');
        return;
      }
      if (event.key === 'Backspace') {
        event.preventDefault();
        this.deleteAtCursor();
        return;
      }
      if (event.key === 'Delete') {
        event.preventDefault();
        if (!this.currentInput || !this.showCursor) {
          this.handleClick('AC');
        } else if (this.cursorPosition < this.currentInput.length) {
          this.currentInput = this.currentInput.slice(0, this.cursorPosition) +
                            this.currentInput.slice(this.cursorPosition + 1);
          if (this.currentInput === '') {
            this.showCursor = false;
            this.cursorPosition = 0;
          }
        }
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        this.handleClick('=');
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        this.handleClick('AC');
        return;
      }
      const keyMap = {
        '0': '0',
        '1': '1',
        '2': '2',
        '3': '3',
        '4': '4',
        '5': '5',
        '6': '6',
        '7': '7',
        '8': '8',
        '9': '9',
        '.': '.',
        '+': '+',
        '-': '-',
        '*': '*',
        '/': '/',
        '=': '=',
        '%': '%',
      };
      const button = keyMap[event.key];
      if (button) {
        event.preventDefault();
        this.handleClick(button);
      }
    },
  },
};
</script>

<style scoped>
div[tabindex] {
  outline: none;
}

div[tabindex]:focus {
  outline: none;
}
</style>
